import { getFileKey, getPlanLookupKey } from "@/lib/file-key";
import {
  notifySessionRegistryChanged,
  sessionRecordFromManifest,
  upsertSessionRecord,
} from "@/lib/storage/session-record-store";
import {
  loadUploadManifest,
  saveUploadManifest,
  type UploadManifest,
  type UploadManifestFile,
} from "@/lib/storage/upload-manifest-store";
import { isClipUploaded } from "@/lib/sessions/phase";
import {
  completeUpload,
  createSession,
  getPartUrls,
  getSessionStatus,
  initUpload,
  UploadApiError,
  type CreateSessionFileInput,
  type CreateSessionFilePlan,
  type CreateSessionResult,
} from "@/lib/upload/upload-api";
import { putWithRetry } from "@/lib/upload/upload-http";
import {
  clearAllLiveProgress,
  clearFileProgress,
  clearPartProgress,
  setPartProgress,
  trackUploadRequest,
} from "@/lib/upload/upload-live-progress";
import { runPool } from "@/lib/upload/upload-pool";
import {
  computeSessionUploadProgress,
  totalPartsForFile,
  type SessionUploadProgress,
} from "@/lib/upload/upload-progress";

const FILE_CONCURRENCY = 2;
const PART_CONCURRENCY = 4;

export type UploadRunOptions = {
  clientSessionId: string;
  files: File[];
  onProgress: (progress: SessionUploadProgress) => void;
  onManifest?: (manifest: UploadManifest) => void;
  signal?: AbortSignal;
};

let progressFrame: number | null = null;
let pendingProgress: { manifest: UploadManifest; onProgress: UploadRunOptions["onProgress"] } | null =
  null;

function scheduleProgress(manifest: UploadManifest, onProgress: UploadRunOptions["onProgress"]) {
  pendingProgress = { manifest, onProgress };
  if (progressFrame !== null) return;

  progressFrame = window.requestAnimationFrame(() => {
    progressFrame = null;
    const pending = pendingProgress;
    pendingProgress = null;
    if (!pending) return;
    pending.onProgress(computeSessionUploadProgress(pending.manifest));
  });
}

function emitProgress(manifest: UploadManifest, onProgress: UploadRunOptions["onProgress"]) {
  onProgress(computeSessionUploadProgress(manifest));
}

function trackPartUpload(
  manifest: UploadManifest,
  manifestFile: UploadManifestFile,
  partNumber: number,
  onProgress: UploadRunOptions["onProgress"],
) {
  return (loaded: number) => {
    setPartProgress(manifestFile.clientFileKey, partNumber, loaded);
    scheduleProgress(manifest, onProgress);
  };
}

function completedPartsFromMap(completed: Map<number, string>) {
  return Array.from(completed.entries())
    .sort(([a], [b]) => a - b)
    .map(([partNumber, etag]) => ({ partNumber, etag }));
}

function buildManifestFile(
  file: File,
  plan: CreateSessionFilePlan,
  existing?: UploadManifestFile,
): UploadManifestFile {
  const built: UploadManifestFile = {
    clientFileKey: getFileKey(file),
    serverFileId: plan.fileId,
    name: file.name,
    sizeBytes: file.size,
    contentType: plan.contentType,
    partSize: plan.partSize,
    multipart: plan.multipart,
    uploadId: null,
    completedParts: [],
    status: "pending",
  };

  if (!existing) return built;

  return {
    ...built,
    uploadId: existing.uploadId,
    completedParts: existing.completedParts,
    status: existing.status,
    error: existing.error,
  };
}

function manifestFileForPlan(
  file: File,
  plan: CreateSessionFilePlan,
  existingManifest?: UploadManifest | null,
) {
  const existing = existingManifest?.files.find(
    (manifestFile) =>
      manifestFile.clientFileKey === getFileKey(file) ||
      (manifestFile.name === file.name && manifestFile.sizeBytes === file.size),
  );

  return buildManifestFile(file, plan, existing);
}

async function persistManifest(
  manifest: UploadManifest,
  onManifest?: (manifest: UploadManifest) => void,
) {
  await saveUploadManifest(manifest);
  onManifest?.(manifest);
}

async function syncSessionRecordFromManifest(
  manifest: UploadManifest,
  options: { notify?: boolean } = {},
) {
  await upsertSessionRecord(sessionRecordFromManifest(manifest));
  if (options.notify !== false) {
    notifySessionRegistryChanged();
  }
}

/**
 * Creates the server session once, mapping the 409 "already exists" conflict to
 * the user-facing message: the session lives on the server but this device no
 * longer holds its token, so it cannot be resumed or re-created here.
 */
async function createSessionOrFail(
  clientSessionId: string,
  files: CreateSessionFileInput[],
): Promise<CreateSessionResult> {
  try {
    return await trackUploadRequest(createSession(clientSessionId, files));
  } catch (error) {
    if (error instanceof UploadApiError && error.status === 409) {
      throw new Error("This session can no longer be resumed on this device.", {
        cause: error,
      });
    }
    throw error;
  }
}

function planByLookupKey(createResult: CreateSessionResult) {
  const planByKey = new Map<string, CreateSessionFilePlan>();
  for (const plan of createResult.files) {
    planByKey.set(`${plan.filename}:${plan.sizeBytes}`, plan);
  }
  return planByKey;
}

/**
 * Seeds the local record with the server-resolved title (the source of truth)
 * and the read-only share token, which is only returned at session creation.
 */
async function seedSessionRecord(
  manifest: UploadManifest,
  createResult: CreateSessionResult,
) {
  await upsertSessionRecord({
    ...sessionRecordFromManifest(manifest),
    title: createResult.title,
    shareToken: createResult.shareToken,
  });
  notifySessionRegistryChanged();
}

/**
 * In-flight createSession calls, keyed by session id. The session can be
 * prepared eagerly (so the share link is available immediately) and started for
 * upload in parallel, so both paths share one creation instead of racing to
 * create two server sessions for the same id.
 */
const sessionCreations = new Map<string, Promise<UploadManifest>>();

async function createServerSession(
  clientSessionId: string,
  files: File[],
): Promise<UploadManifest> {
  const existing = await loadUploadManifest(clientSessionId);

  const createResult = await createSessionOrFail(
    clientSessionId,
    files.map((file) => ({
      filename: file.name,
      sizeBytes: file.size,
      contentType: file.type || "video/mp4",
    })),
  );

  if (!createResult.sessionToken) {
    throw new Error("Missing session token from upload API.");
  }

  if (createResult.sessionId !== clientSessionId) {
    throw new Error("Upload session ID mismatch.");
  }

  const planByKey = planByLookupKey(createResult);

  const manifest: UploadManifest = {
    clientSessionId,
    sessionToken: createResult.sessionToken,
    createdAt: existing?.createdAt ?? Date.now(),
    files: files.map((file) => {
      const plan =
        planByKey.get(getPlanLookupKey(file)) ??
        createResult.files.find((candidate) => candidate.filename === file.name);
      if (!plan) {
        throw new Error(`Missing upload plan for ${file.name}`);
      }
      return manifestFileForPlan(file, plan, existing);
    }),
  };

  await saveUploadManifest(manifest);
  await seedSessionRecord(manifest, createResult);
  return manifest;
}

async function ensureSession(options: UploadRunOptions): Promise<UploadManifest> {
  // loadUploadManifest hydrates the token from the session record (its single
  // home), so a populated token means the session can resume without being
  // re-created, which the server now rejects with a 409 and which would
  // otherwise invalidate the token held elsewhere.
  const existing = await loadUploadManifest(options.clientSessionId);
  if (existing?.sessionToken) {
    options.onManifest?.(existing);
    return existing;
  }

  const inflight = sessionCreations.get(options.clientSessionId);
  if (inflight) {
    const manifest = await inflight;
    options.onManifest?.(manifest);
    return manifest;
  }

  const creation = createServerSession(
    options.clientSessionId,
    options.files,
  ).finally(() => sessionCreations.delete(options.clientSessionId));
  sessionCreations.set(options.clientSessionId, creation);

  const manifest = await creation;
  options.onManifest?.(manifest);
  return manifest;
}

/**
 * Creates the server session (and seeds the local record with the share token)
 * without starting the byte transfer, so the share link is available right
 * away. The upload itself begins later and reuses this session.
 */
export async function prepareServerSession(
  clientSessionId: string,
  files: File[],
): Promise<UploadManifest> {
  return ensureSession({ clientSessionId, files, onProgress: () => {} });
}

/**
 * Re-creates the server session after its token was rejected. A 401 means the
 * server no longer recognizes this session (it was deleted, or the backend lost
 * it), so the stored token can never upload again. Re-creating recovers the
 * common case where the session is simply gone; the new session has fresh file
 * ids, so any locally recorded upload progress is reset and every file
 * re-uploads. If the session still exists under a token this device no longer
 * holds, createSession returns 409 and the unresumable error surfaces instead.
 */
async function recreateServerSession(
  existing: UploadManifest,
  onManifest?: (manifest: UploadManifest) => void,
): Promise<UploadManifest> {
  const createResult = await createSessionOrFail(
    existing.clientSessionId,
    existing.files.map((file) => ({
      filename: file.name,
      sizeBytes: file.sizeBytes,
      contentType: file.contentType,
    })),
  );

  if (!createResult.sessionToken) {
    throw new Error("Missing session token from upload API.");
  }

  const planByKey = planByLookupKey(createResult);

  const manifest: UploadManifest = {
    clientSessionId: existing.clientSessionId,
    sessionToken: createResult.sessionToken,
    createdAt: existing.createdAt,
    files: existing.files.map((file) => {
      const plan =
        planByKey.get(`${file.name}:${file.sizeBytes}`) ??
        createResult.files.find((candidate) => candidate.filename === file.name);
      if (!plan) {
        throw new Error(`Missing upload plan for ${file.name}`);
      }
      // A new session means fresh server file ids and uploads, so any progress
      // recorded against the old session no longer applies and is reset.
      return {
        ...file,
        serverFileId: plan.fileId,
        partSize: plan.partSize,
        multipart: plan.multipart,
        uploadId: null,
        completedParts: [],
        status: "pending",
        error: undefined,
      };
    }),
  };

  await persistManifest(manifest, onManifest);
  await seedSessionRecord(manifest, createResult);
  return manifest;
}

async function uploadSingleFilePut(
  file: File,
  manifest: UploadManifest,
  manifestFile: UploadManifestFile,
  putUrl: string,
  options: UploadRunOptions,
) {
  manifestFile.status = "uploading";
  manifestFile.uploadId = "single";
  await persistManifest(manifest, options.onManifest);
  emitProgress(manifest, options.onProgress);

  const etag = await putWithRetry(
    putUrl,
    file,
    options.signal,
    trackPartUpload(manifest, manifestFile, 1, options.onProgress),
  );
  clearPartProgress(manifestFile.clientFileKey, 1);
  manifestFile.completedParts = [{ partNumber: 1, etag }];
  await trackUploadRequest(
    completeUpload(manifest.clientSessionId, manifestFile.serverFileId, manifest.sessionToken, {
      parts: manifestFile.completedParts.map((part) => ({
        partNumber: part.partNumber,
        etag: part.etag,
      })),
    }),
  );
  manifestFile.status = "uploaded";
  manifestFile.error = undefined;
  await persistManifest(manifest, options.onManifest);
  emitProgress(manifest, options.onProgress);
}

async function uploadMultipartFile(
  file: File,
  manifest: UploadManifest,
  manifestFile: UploadManifestFile,
  options: UploadRunOptions,
) {
  let init = await trackUploadRequest(
    initUpload(
      manifest.clientSessionId,
      manifestFile.serverFileId,
      manifest.sessionToken,
    ),
  );

  // Part etags are only valid for the upload they were uploaded against. If the
  // server handed back a different multipart upload (e.g. the previous one was
  // aborted after a failed completion), the stored etags belong to an upload
  // that no longer exists. Discard them so every part is re-uploaded against the
  // new upload instead of completing it with parts it never received, which S3
  // rejects with InvalidPart.
  if (manifestFile.uploadId && manifestFile.uploadId !== init.uploadId) {
    manifestFile.completedParts = [];
    clearFileProgress(manifestFile.clientFileKey);
  }

  manifestFile.status = "uploading";
  manifestFile.partSize = init.partSize;
  manifestFile.multipart = init.multipart;
  manifestFile.uploadId = init.uploadId;
  await persistManifest(manifest, options.onManifest);
  emitProgress(manifest, options.onProgress);

  const totalParts = totalPartsForFile(file.size, init.partSize);
  const completed = new Map(
    manifestFile.completedParts.map((part) => [part.partNumber, part.etag] as const),
  );

  const remaining = () =>
    Array.from({ length: totalParts }, (_, index) => index + 1).filter(
      (partNumber) => !completed.has(partNumber),
    );

  while (remaining().length > 0) {
    if (options.signal?.aborted) {
      throw new DOMException("Upload aborted", "AbortError");
    }

    const batch = remaining().slice(0, PART_CONCURRENCY);
    let partUrls;
    try {
      partUrls = await trackUploadRequest(
        getPartUrls(
          manifest.clientSessionId,
          manifestFile.serverFileId,
          manifest.sessionToken,
          batch,
        ),
      );
    } catch (error) {
      if (error instanceof UploadApiError && error.status === 403) {
        init = await trackUploadRequest(
          initUpload(
            manifest.clientSessionId,
            manifestFile.serverFileId,
            manifest.sessionToken,
          ),
        );
        manifestFile.uploadId = init.uploadId;
        manifestFile.partSize = init.partSize;
        manifestFile.completedParts = [];
        completed.clear();
        clearFileProgress(manifestFile.clientFileKey);
        await persistManifest(manifest, options.onManifest);
        continue;
      }
      throw error;
    }

    await Promise.all(
      partUrls.map(async ({ partNumber, url }) => {
        const start = (partNumber - 1) * init.partSize;
        const end = Math.min(start + init.partSize, file.size);
        const blob = file.slice(start, end);
        const etag = await putWithRetry(
          url,
          blob,
          options.signal,
          trackPartUpload(manifest, manifestFile, partNumber, options.onProgress),
        );
        clearPartProgress(manifestFile.clientFileKey, partNumber);
        completed.set(partNumber, etag);
        manifestFile.completedParts = completedPartsFromMap(completed);
        // Checkpoint after every completed part so a refresh resumes from the
        // last finished part instead of restarting the in-flight batch.
        await persistManifest(manifest, options.onManifest);
        emitProgress(manifest, options.onProgress);
      }),
    );
  }

  await trackUploadRequest(
    completeUpload(manifest.clientSessionId, manifestFile.serverFileId, manifest.sessionToken, {
      uploadId: manifestFile.uploadId,
      parts: manifestFile.completedParts.map((part) => ({
        partNumber: part.partNumber,
        etag: part.etag,
      })),
    }),
  );

  manifestFile.status = "uploaded";
  manifestFile.error = undefined;
  await persistManifest(manifest, options.onManifest);
  emitProgress(manifest, options.onProgress);
}

async function uploadOneFile(
  file: File,
  manifest: UploadManifest,
  options: UploadRunOptions,
) {
  const manifestFile = manifest.files.find(
    (candidate) => candidate.clientFileKey === getFileKey(file),
  );
  if (!manifestFile || manifestFile.status === "uploaded") return;

  manifestFile.error = undefined;

  try {
    const init = await trackUploadRequest(
      initUpload(
        manifest.clientSessionId,
        manifestFile.serverFileId,
        manifest.sessionToken,
      ),
    );

    if (!init.multipart && init.putUrl) {
      await uploadSingleFilePut(file, manifest, manifestFile, init.putUrl, options);
      return;
    }

    await uploadMultipartFile(file, manifest, manifestFile, options);
  } catch (error) {
    // A pause aborts in-flight transfers. That isn't a failure: completed parts
    // are already checkpointed, so reset to pending (not failed) to keep the
    // clip resumable and avoid surfacing a red error / retry affordance.
    if (error instanceof DOMException && error.name === "AbortError") {
      manifestFile.status = "pending";
      manifestFile.error = undefined;
      clearFileProgress(manifestFile.clientFileKey);
      await persistManifest(manifest, options.onManifest);
      emitProgress(manifest, options.onProgress);
      throw error;
    }
    manifestFile.status = "failed";
    manifestFile.error = error instanceof Error ? error.message : "Upload failed";
    clearFileProgress(manifestFile.clientFileKey);
    await persistManifest(manifest, options.onManifest);
    emitProgress(manifest, options.onProgress);
    throw error;
  }
}

const inflightUploads = new Map<string, Promise<UploadManifest>>();

/**
 * Serializes upload runs per session. Pausing aborts the active run, which then
 * unwinds asynchronously (resetting its in-flight clips back to pending). If a
 * resume arrives before that unwind finishes, this waits for the aborted run to
 * settle and then starts a *fresh* run bound to the new abort signal, instead of
 * handing back the already-aborted run, whose dead signal would let no bytes
 * ever move again.
 */
export async function runSessionUpload(options: UploadRunOptions) {
  const previous = inflightUploads.get(options.clientSessionId);

  const run = (async () => {
    if (previous) await previous.catch(() => {});
    return runSessionUploadOnce(options);
  })();

  const tracked = run.finally(() => {
    if (inflightUploads.get(options.clientSessionId) === tracked) {
      inflightUploads.delete(options.clientSessionId);
    }
  });
  inflightUploads.set(options.clientSessionId, tracked);
  return tracked;
}

/**
 * Reconciles a resumed manifest against the server, which is the source of
 * truth for what has finished uploading. Files the server already has are
 * marked uploaded locally so we never re-init or re-PUT parts for a multipart
 * upload that was already completed (which makes S3 return 404 NoSuchUpload).
 */
type ReconcileResult = "ok" | "session-gone";

async function reconcileManifestWithServer(
  manifest: UploadManifest,
  options: UploadRunOptions,
): Promise<ReconcileResult> {
  let status;
  try {
    status = await trackUploadRequest(
      getSessionStatus(manifest.clientSessionId, manifest.sessionToken),
    );
  } catch (error) {
    // A 401 means the server no longer accepts this session's token, so the
    // session has to be re-created before anything can upload. Other errors are
    // transient; skip reconciliation and let the upload proceed (and retry).
    if (error instanceof UploadApiError && error.status === 401) {
      return "session-gone";
    }
    console.error("Failed to reconcile upload status with server", error);
    return "ok";
  }

  const serverPhaseByFileId = new Map(
    status.files.map((file) => [file.fileId, file.phase] as const),
  );

  let changed = false;
  for (const manifestFile of manifest.files) {
    const serverPhase = serverPhaseByFileId.get(manifestFile.serverFileId);
    if (
      serverPhase &&
      isClipUploaded(serverPhase) &&
      manifestFile.status !== "uploaded"
    ) {
      manifestFile.status = "uploaded";
      manifestFile.error = undefined;
      changed = true;
    }
  }

  if (changed) {
    await persistManifest(manifest, options.onManifest);
  }

  return "ok";
}

async function runSessionUploadOnce(options: UploadRunOptions) {
  clearAllLiveProgress();
  let manifest = await ensureSession(options);

  // Always reconcile against the server (source of truth for what is already
  // stored) before uploading. This prevents re-uploading files that finished
  // in a previous run, even when the local manifest was rebuilt from scratch.
  if ((await reconcileManifestWithServer(manifest, options)) === "session-gone") {
    // The stored token is dead. Rebuild the session so an explicit retry (and
    // auto-resume) can recover instead of looping on "Invalid session token".
    manifest = await recreateServerSession(manifest, options.onManifest);
  }

  emitProgress(manifest, options.onProgress);

  const pendingFiles = options.files.filter((file) => {
    const manifestFile = manifest.files.find(
      (candidate) => candidate.clientFileKey === getFileKey(file),
    );
    return manifestFile?.status !== "uploaded";
  });

  const errors: Error[] = [];

  await runPool(pendingFiles, FILE_CONCURRENCY, async (file) => {
    // A pause aborts the run: skip files that haven't started yet instead of
    // initiating a server upload for each only to abort it, so the run unwinds
    // immediately and a resume can start a fresh run.
    if (options.signal?.aborted) return;
    try {
      await uploadOneFile(file, manifest, options);
    } catch (error) {
      errors.push(error instanceof Error ? error : new Error("Upload failed"));
    }
  });

  // A pause surfaces as a clean abort (not an upload failure) so the manager
  // leaves the session paused and resumable rather than flipping it to "error".
  if (options.signal?.aborted) {
    throw new DOMException("Upload aborted", "AbortError");
  }

  const progress = computeSessionUploadProgress(manifest);
  clearAllLiveProgress();
  emitProgress(manifest, options.onProgress);

  if (errors.length > 0 && progress.filesComplete < progress.filesTotal) {
    await syncSessionRecordFromManifest(manifest);
    throw errors[0];
  }

  await syncSessionRecordFromManifest(manifest);
  return manifest;
}

export async function retryFileUpload(
  file: File,
  manifest: UploadManifest,
  options: Omit<UploadRunOptions, "clientSessionId" | "files">,
) {
  const manifestFile = manifest.files.find(
    (candidate) => candidate.clientFileKey === getFileKey(file),
  );
  if (!manifestFile) return manifest;

  manifestFile.status = "pending";
  manifestFile.error = undefined;
  await persistManifest(manifest, options.onManifest);

  await uploadOneFile(file, manifest, {
    ...options,
    clientSessionId: manifest.clientSessionId,
    files: [file],
  });

  return manifest;
}
